"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
import { createPortal } from "react-dom";

interface ActionBarPositioning {
  /**
   * The gutter from the edge in pixels.
   *
   * @default '16px'
   */
  gutter?: string;
  /**
   * The placement of the action bar.
   *
   * @default "bottom"
   */
  placement?: "bottom" | "bottom-start" | "bottom-end";
}

interface ActionBarContextValue {
  /**
   * The open state of the action bar
   */
  isOpen?: boolean;
  /**
   * Whether to lazy mount the action bar
   */
  lazyMount?: boolean;
  /**
   * The function to call when the action bar is closed
   */
  onClose?: () => void;
  /**
   * The function to call when the action bar is opened
   */
  onOpen?: () => void;
  /**
   * The positioning of the action bar.
   */
  positioning: ActionBarPositioning;
  /**
   * The function to call when the action bar is mounted
   */
  unmountOnExit?: boolean;
}

const ActionBarContext = React.createContext({} as ActionBarContextValue);

export interface ActionBarProps
  extends Pick<ActionBarContextValue, "lazyMount" | "unmountOnExit"> {
  /**
   * Whether to close the action bar when the Escape key is pressed.
   *
   * @default true
   */
  closeOnEscape?: boolean;
  /**
   * The default open state of the action bar.
   */
  defaultOpen?: boolean;
  /**
   * The function to call when the open state of the action bar changes.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * The open state of the action bar.
   */
  open?: boolean;
  /**
   * Placement and gutter of the action bar.
   */
  positioning?: ActionBarContextValue["positioning"];
}

const defaultPositioning = { gutter: "16px", placement: "bottom" } as const;

/**
 * Floating action bar for operating on a selection (bulk actions). Portals a
 * dismissible toolbar pinned to the bottom of the viewport with enter/exit
 * animation. Controlled (`open` / `onOpenChange`) or uncontrolled
 * (`defaultOpen`); closes on Escape by default.
 *
 * Anatomy:
 * ```
 * ActionBar
 * ├── ActionBarTrigger          (opens the bar)
 * └── ActionBarContent          (portaled, role="toolbar")
 *     ├── ActionBarValue        (selection count)
 *     ├── ActionBarSeparator
 *     ├── ActionBarBody         (primary actions)
 *     ├── ActionBarSeparator
 *     └── ActionBarClose        (dismiss)
 * ```
 *
 * Position with `positioning={{ placement: "bottom" | "bottom-start" |
 * "bottom-end", gutter: "16px" }}`. Tune inner spacing via the
 * `[--space:--spacing(2)]` CSS variable on `ActionBarContent`. Trigger,
 * content, body and close accept a `render` element for composition.
 *
 * @example
 * <ActionBar open={selected.length > 0} onOpenChange={clearSelection}>
 *   <ActionBarContent>
 *     <ActionBarValue count={selected.length} />
 *     <ActionBarSeparator />
 *     <ActionBarBody>
 *       <Button render={<button type="button" />} variant="ghost">
 *         <Pencil />
 *         <span className="max-sm:sr-only">Edit</span>
 *       </Button>
 *       <ActionBarSeparator />
 *       <Button variant="destructive">
 *         <Trash2 />
 *         <span className="max-sm:sr-only">Delete</span>
 *       </Button>
 *     </ActionBarBody>
 *     <ActionBarSeparator />
 *     <ActionBarClose render={<Button size="icon-sm" variant="ghost" />}>
 *       <X />
 *     </ActionBarClose>
 *   </ActionBarContent>
 * </ActionBar>
 */
export const ActionBar = (props: React.PropsWithChildren<ActionBarProps>) => {
  const {
    open,
    defaultOpen = false,
    closeOnEscape = true,
    positioning,
    lazyMount = true,
    unmountOnExit = true,
    onOpenChange,
    ...rest
  } = props;

  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleClose = React.useCallback(() => {
    if (!isControlled) {
      setInternalOpen(false);
    }

    onOpenChange?.(false);
  }, [isControlled, onOpenChange]);

  const handleOpen = React.useCallback(() => {
    if (!isControlled) {
      setInternalOpen(true);
    }

    onOpenChange?.(true);
  }, [isControlled, onOpenChange]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!closeOnEscape) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      if (event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      handleClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeOnEscape, handleClose, isOpen]);

  const context = React.useMemo(
    () => ({
      isOpen,
      lazyMount,
      onClose: handleClose,
      onOpen: handleOpen,
      positioning: { ...defaultPositioning, ...positioning },
      unmountOnExit,
    }),
    [handleClose, handleOpen, isOpen, lazyMount, unmountOnExit, positioning]
  );

  return <ActionBarContext.Provider value={context} {...rest} />;
};

export type ActionBarTriggerProps = useRender.ComponentProps<"button">;

export const ActionBarTrigger = ({
  render,
  ...props
}: ActionBarTriggerProps) => {
  const { onOpen, isOpen } = _useActionBar();

  const defaultProps = {
    "aria-expanded": isOpen,
    "data-slot": "action-bar-trigger",
    "data-state": isOpen ? "open" : "closed",
    onClick: () => onOpen?.(),
    type: "button" as const,
  };

  return useRender({
    defaultTagName: "button",
    props: mergeProps(defaultProps, props),
    render,
  });
};

const actionBarPositionerVariants = cva(
  "fixed inset-x-0 bottom-0 z-50 flex px-4 pb-[calc(var(--gutter)+env(safe-area-inset-bottom,0))] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-2 motion-reduce:animate-none!",
  {
    defaultVariants: { placement: "bottom" },
    variants: {
      placement: {
        bottom: "justify-center",
        "bottom-end": "justify-end",
        "bottom-start": "justify-start",
      },
    },
  }
);

export type ActionBarContentProps = useRender.ComponentProps<"div">;

const ActionBarContentInner = ({
  className,
  render,
  ...props
}: ActionBarContentProps) => {
  const defaultProps = {
    className: cn(
      "[--space:--spacing(2)] flex w-fit items-center gap-1 rounded-xl border bg-popover px-[calc(var(--space)+2px)] py-(--space) text-popover-foreground shadow-lg/5",
      className
    ),
    "data-slot": "action-bar-content",
    role: "toolbar" as const,
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps(defaultProps, props),
    render,
  });
};

export const ActionBarContent = (props: ActionBarContentProps) => {
  const { isOpen, lazyMount, unmountOnExit, positioning } = _useActionBar();

  const placement = positioning.placement ?? "bottom";
  const gutter = positioning.gutter ?? "16px";

  const [mounted, setMounted] = React.useState(() =>
    lazyMount ? Boolean(isOpen) : true
  );
  const [container, setContainer] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    setContainer(document.body);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      setMounted(true);
    }
  }, [isOpen]);

  if (!(mounted && container)) {
    return null;
  }

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (!isOpen && unmountOnExit) {
      setMounted(false);
    }
  };

  return createPortal(
    <div
      className={cn(actionBarPositionerVariants({ placement }))}
      data-placement={placement}
      data-slot="action-bar-positioner"
      data-state={isOpen ? "open" : "closed"}
      onAnimationEnd={handleAnimationEnd}
      style={{ "--gutter": gutter } as React.CSSProperties}
    >
      <ActionBarContentInner {...props} />
    </div>,
    container
  );
};

export type ActionBarSeparatorProps = React.ComponentProps<typeof Separator>;

export const ActionBarSeparator = ({
  className,
  ...props
}: ActionBarSeparatorProps) => {
  return (
    <Separator
      className={cn("mx-1 h-1/2", className)}
      data-slot="action-bar-separator"
      orientation="vertical"
      {...props}
    />
  );
};

export type ActionBarCloseProps = useRender.ComponentProps<"button">;

export const ActionBarClose = ({ className, render, ...props }: ActionBarCloseProps) => {
  const { onClose, isOpen } = _useActionBar();

  const defaultProps = {
    "aria-label": "Close",
    className: cn(
      "opacity-64 transition-opacity hover:opacity-100 motion-reduce:transition-none!",
      className
    ),
    "data-slot": "action-bar-close",
    "data-state": isOpen ? "open" : "closed",
    onClick: () => onClose?.(),
    type: "button" as const,
  };

  return useRender({
    defaultTagName: "button",
    props: mergeProps(defaultProps, props),
    render,
  });
};

export interface ActionBarValueProps
  extends React.ComponentProps<typeof Badge> {
  /**
   * The number of items selected
   */
  count: number;
  /**
   * The label of the selection trigger
   */
  label?: string;
}

export const ActionBarValue = ({
  label,
  count = 0,
  className,
  children,
  ...props
}: ActionBarValueProps) => {
  return (
    <Badge
      className={cn("shrink-0 font-medium text-sm tabular-nums", className)}
      data-slot="action-bar-value"
      variant="secondary"
      {...props}
    >
      {children ?? label ?? count}
    </Badge>
  );
};

export type ActionBarBodyProps = useRender.ComponentProps<"div">;

export const ActionBarBody = ({
  className,
  render,
  ...props
}: ActionBarBodyProps) => {
  const defaultProps = {
    className: cn(
      "flex items-center gap-1 **:data-[slot=action-bar-separator]:h-2",
      className
    ),
    "data-slot": "action-bar-body",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps(defaultProps, props),
    render,
  });
};

const _useActionBar = () => {
  const context = React.useContext(ActionBarContext);

  if (!context) {
    throw new Error("useActionBar must be used within a ActionBarProvider.");
  }

  return context;
};
